import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import EmptyState from '@/components/common/EmptyState'
import ListPagination, { ListSummary } from '@/components/common/ListPagination'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDateTime } from '@/lib/format'
import { cycleSort, type SortOrder, type SortState } from '@/lib/sort-url'
import { cn } from '@/lib/utils'

export type Column<T> = {
  key: string
  header: string
  /** Tooltip for abbreviated headers. */
  headerTitle?: string
  /** Group label drawn above adjacent columns that share it (AccSum: Balance, Margin, ...). */
  group?: string
  cell: (row: T) => ReactNode
  align?: 'left' | 'right'
  /** Identity column that stays visible while scrolling sideways. */
  sticky?: boolean
  /** Role in the card layout below 640px. Default 'hidden'. */
  mobile?: 'title' | 'subtitle' | 'amount' | 'status' | 'field' | 'actions' | 'hidden'
  /** Extra classes on the header and body cells, e.g. a fixed width. */
  className?: string
  /** Exact column box width in px, applied to the header and the cell. */
  width?: number
  /** Server sort key. Used when onSortChange is set. */
  sortKey?: string
  /** First direction when this column is chosen. Dates and amounts use desc. */
  sortFirst?: SortOrder
  /** Client-side sort key (small lists only). */
  sortValue?: (row: T) => string | number | null
}

export type Paging = {
  total: number
  page: number
  pageSize: number
  onPage: (page: number) => void
  /** Required, so a page can't ship a dead page-size selector. */
  onPageSize: (size: number) => void
  pageSizeOptions?: number[]
}

interface Props<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  /** Omit for short lists that show everything. */
  paging?: Paging
  onRowClick?: (row: T, el: HTMLElement) => void
  selectedKey?: string | null
  loading?: boolean
  /** Query error. Shown instead of the empty state, so a failed load never reads as "no rows". */
  error?: unknown
  dimmed?: boolean
  emptyTitle?: string
  emptyDescription?: string
  /** Server sort. When onSortChange is set, rows stay in server order. */
  sort?: SortState
  onSortChange?: (next: SortState) => void
  /** Extra classes on the desktop table, e.g. a fixed layout. */
  tableClassName?: string
  /** Zebra rows, for wide numeric tables where the eye loses its row. */
  striped?: boolean
  /**
   * Desktop columns for the scroll wrapper's content width.
   * `shown` is the tier already on screen, so the caller can hold a tier across a small resize.
   */
  visibleKeys?: (shown: readonly string[], availablePx: number) => readonly string[]
}

const cellBase = 'px-3 py-2 whitespace-nowrap'

function Empty({ children }: { children?: ReactNode }) {
  return (
    <span className="text-muted-foreground">{children ?? '—'}</span>
  )
}

/** "—" is muted; anything else is shown as is. */
export function muted(v: ReactNode): ReactNode {
  return v === '—' || v == null || v === '' ? <Empty /> : v
}

/** Date over time, same box as Funds Created. The title is the full Jakarta value. */
export function StackedDateTime({ value }: { value?: string | null }) {
  const full = fmtDateTime(value)
  if (full === '—') return muted(full)
  const [date, time] = full.split(', ')
  return (
    <div className="overflow-hidden whitespace-normal" title={full}>
      <div className="truncate">{date}</div>
      {time ? <div className="truncate text-[12.5px] text-muted-foreground">{time}</div> : null}
    </div>
  )
}

/**
 * Date and time on one line, for columns where some rows are empty: a stacked date would make those rows
 * taller than the rest. The time is grey and monospaced so the two parts stay distinct; WIB goes in the header.
 */
export function InlineDateTime({ value }: { value?: string | null }) {
  const full = fmtDateTime(value)
  if (full === '—') return muted(full)
  const [date, time = ''] = full.split(', ')
  return (
    <span className="whitespace-nowrap" title={full}>
      {date}
      <span className="ml-2 font-mono text-[12.5px] text-muted-foreground">{time.replace(' WIB', '')}</span>
    </span>
  )
}

const warnedWidths = new Set<string>()

function warnMissingWidths(columns: { key: string; sortKey?: string; width?: number }[]) {
  if (!import.meta.env.DEV) return
  if (!columns.some((c) => c.sortKey)) return
  if (columns.every((c) => c.width != null)) return
  const id = columns.map((c) => c.key).join('\0')
  if (warnedWidths.has(id)) return
  warnedWidths.add(id)
  const missing = columns.filter((c) => c.width == null).map((c) => c.key)
  console.warn(`DeskTable has a sortable column but no width on: ${missing.join(', ')}`)
}

function cmpSort(a: string | number | null | undefined, b: string | number | null | undefined, dir: 1 | -1): number {
  const aNull = a == null
  const bNull = b == null
  if (aNull || bNull) {
    if (aNull && bNull) return 0
    return aNull ? 1 : -1
  }
  const cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true })
  return cmp * dir
}

function SortIcon({ active, order }: { active: boolean; order?: SortOrder }) {
  const cls = 'h-3 w-3'
  if (!active) return <ArrowUpDown className={cn(cls, 'opacity-60')} aria-hidden />
  return order === 'asc' ? <ArrowUp className={cls} aria-hidden /> : <ArrowDown className={cls} aria-hidden />
}

/** Runs of adjacent columns that share a group label. */
function groupRuns<T>(cols: Column<T>[]): { group?: string; span: number; first: Column<T> }[] {
  const runs: { group?: string; span: number; first: Column<T> }[] = []
  for (const c of cols) {
    const last = runs[runs.length - 1]
    if (last && c.group && last.group === c.group) last.span += 1
    else runs.push({ group: c.group, span: 1, first: c })
  }
  return runs
}

export function DeskTable<T>({
  columns,
  rows: rowsIn,
  rowKey,
  paging,
  onRowClick,
  selectedKey,
  loading,
  error,
  dimmed,
  emptyTitle = 'No rows',
  emptyDescription = 'Try another filter.',
  sort: sortProp = null,
  onSortChange,
  tableClassName,
  striped,
  visibleKeys,
}: Props<T>) {
  warnMissingWidths(columns)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [desktopKeys, setDesktopKeys] = useState<readonly string[] | null>(null)
  useEffect(() => {
    const el = wrapRef.current
    if (!el || !visibleKeys) return
    const read = () => {
      const style = getComputedStyle(el)
      const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
      const available = el.clientWidth - pad
      setDesktopKeys((shown) => {
        const prev = shown ?? visibleKeys([], 0)
        const next = visibleKeys(prev, available)
        if (prev.length === next.length && prev.every((key, i) => key === next[i])) return prev
        return next
      })
    }
    read()
    const observer = new ResizeObserver(read)
    observer.observe(el)
    return () => observer.disconnect()
  }, [visibleKeys, loading, rowsIn.length])
  const [localSort, setLocalSort] = useState<SortState>(null)
  const sort = onSortChange ? sortProp : localSort
  const sortCol = sort ? columns.find((c) => c.key === sort.key) : undefined
  const rows = useMemo(() => {
    if (onSortChange || !sort || !sortCol?.sortValue) return rowsIn
    const read = sortCol.sortValue
    const dir = sort.order === 'asc' ? 1 : -1
    return [...rowsIn].sort((a, b) => cmpSort(read(a), read(b), dir))
  }, [rowsIn, sort, sortCol, onSortChange])
  const by = (role: NonNullable<Column<T>['mobile']>) => columns.filter((c) => c.mobile === role)
  const [title] = by('title')
  const [subtitle] = by('subtitle')
  const [amount] = by('amount')
  const [status] = by('status')
  const fields = by('field').slice(0, 3)
  const [actions] = by('actions')
  const empty = !loading && rows.length === 0
  const shownKeys = visibleKeys ? (desktopKeys ?? visibleKeys([], 0)) : null
  const desktopCols = shownKeys ? columns.filter((c) => shownKeys.includes(c.key)) : columns
  const sized = columns.length > 0 && columns.every((c) => c.width != null)
  const tableMinWidth = sized ? desktopCols.reduce((sum, c) => sum + (c.width ?? 0), 0) : undefined
  const grouped = desktopCols.some((c) => c.group)
  const groupStart = new Set(groupRuns(desktopCols).filter((run) => run.group).map((run) => run.first.key))

  const activate = (row: T) => (e: { currentTarget: HTMLElement }) => onRowClick?.(row, e.currentTarget)

  const headCell = (c: Column<T>, extra?: { rowSpan?: number; className?: string }) => {
    const sortable = Boolean(onSortChange ? c.sortKey : c.sortValue)
    const active = Boolean(sort && sort.key === c.key)
    const ariaSort = sortable ? (active ? (sort?.order === 'asc' ? 'ascending' : 'descending') : 'none') : undefined
    return (
      <th
        key={c.key}
        title={c.headerTitle}
        scope="col"
        rowSpan={extra?.rowSpan}
        aria-sort={ariaSort}
        style={c.width ? { width: c.width } : undefined}
        className={cn(
          'h-9 border-b border-border-strong bg-muted px-3 font-semibold whitespace-nowrap',
          c.width != null && 'overflow-hidden',
          c.align === 'right' && 'text-right',
          groupStart.has(c.key) && 'border-l border-l-border-strong',
          c.sticky && 'sticky left-0 z-10 shadow-[1px_0_0_var(--border-strong)]',
          extra?.className,
          c.className,
        )}
      >
        {sortable ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            aria-label={`Sort by ${c.headerTitle ?? c.header}`}
            onClick={() => {
              const next = cycleSort(active ? sort : null, c.key, c.sortFirst ?? 'asc')
              if (onSortChange) onSortChange(next)
              else setLocalSort(next)
            }}
          >
            {c.header}
            <span className="inline-flex size-4 shrink-0 items-center justify-center">
              <SortIcon active={active} order={sort?.order} />
            </span>
            {active ? <span className="sr-only">{sort?.order === 'asc' ? '(sorted ascending)' : '(sorted descending)'}</span> : null}
          </button>
        ) : (
          c.header
        )}
      </th>
    )
  }

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-border bg-card" aria-busy={loading || undefined}>
        {loading ? (
          <div className="space-y-3 p-4">
            <p className="text-sm text-muted-foreground">Loading…</p>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-3.5" style={{ width: `${92 - (i % 3) * 8}%` }} />
            ))}
          </div>
        ) : error && rows.length === 0 ? (
          <div role="alert" className="flex flex-col items-start gap-2 px-6 py-8">
            <h2 className="text-base font-semibold">Could not load this list</h2>
            <p className="max-w-prose text-sm text-muted-foreground">
              {error instanceof Error && error.message ? `${error.message}. ` : ''}This does not mean the list is empty. Reload the page to try again, and tell the platform team if it keeps failing.
            </p>
          </div>
        ) : empty ? (
          <EmptyState title={emptyTitle} description={emptyDescription} />
        ) : (
          <>
            {error ? (
              <p role="alert" className="border-b border-border bg-[var(--tone-danger-bg)] px-4 py-2 text-sm font-semibold text-[var(--tone-danger-fg)]">
                Could not refresh this list{error instanceof Error && error.message ? `: ${error.message}` : ''}. These rows may be out of date.
              </p>
            ) : null}
            <div ref={wrapRef} className={cn('overflow-x-auto max-sm:hidden', dimmed && 'opacity-60')}>
              <table
                className={cn('min-w-full border-separate border-spacing-0 text-left text-sm', sized && 'table-fixed w-full', tableClassName)}
                style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}
              >
                <thead className="text-[13px] text-muted-foreground">
                  {grouped ? (
                    <>
                      <tr>
                        {groupRuns(desktopCols).map((run) =>
                          run.group ? (
                            <th
                              key={`g-${run.first.key}`}
                              scope="colgroup"
                              colSpan={run.span}
                              className="h-7 border-b border-l border-border border-l-border-strong bg-muted px-3 text-center text-[12.5px] font-semibold"
                            >
                              {run.group}
                            </th>
                          ) : (
                            headCell(run.first, { rowSpan: 2, className: 'align-bottom pb-2' })
                          ),
                        )}
                      </tr>
                      <tr>{desktopCols.filter((c) => c.group).map((c) => headCell(c))}</tr>
                    </>
                  ) : (
                    <tr>{desktopCols.map((c) => headCell(c))}</tr>
                  )}
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const k = rowKey(row)
                    const selected = Boolean(onRowClick) && k === selectedKey
                    const rowBg = selected ? 'bg-selected' : striped && index % 2 === 1 ? 'bg-stripe' : 'bg-card'
                    return (
                      <tr
                        key={k}
                        aria-current={selected ? 'true' : undefined}
                        tabIndex={onRowClick ? 0 : undefined}
                        onClick={onRowClick ? activate(row) : undefined}
                        onKeyDown={
                          onRowClick
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  activate(row)(e)
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          'group',
                          rowBg,
                          onRowClick && 'cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                        )}
                      >
                        {desktopCols.map((c) => (
                          <td
                            key={c.key}
                            style={c.width ? { width: c.width } : undefined}
                            className={cn(
                              cellBase,
                              'border-b border-border group-last:border-b-0 group-hover:bg-muted',
                              c.align === 'right' && c.mobile !== 'actions' && 'text-right font-mono tabular-nums',
                              c.align === 'right' && c.mobile === 'actions' && 'text-right',
                              groupStart.has(c.key) && 'border-l border-l-border-strong',
                              c.sticky && cn('sticky left-0 shadow-[1px_0_0_var(--border-strong)]', rowBg),
                              c.className,
                            )}
                          >
                            {c.cell(row)}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <ul className={cn('divide-y divide-border sm:hidden', dimmed && 'opacity-60')}>
              {rows.map((row) => {
                const k = rowKey(row)
                const body = (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={cn('font-semibold break-words', title?.align === 'right' && 'font-mono')}>{title?.cell(row)}</div>
                        {subtitle ? <div className="text-[13.5px] text-muted-foreground">{subtitle.cell(row)}</div> : null}
                      </div>
                      {amount ? <div className="shrink-0 font-mono font-semibold tabular-nums">{amount.cell(row)}</div> : null}
                    </div>
                    {status ? <div className="mt-1.5">{status.cell(row)}</div> : null}
                    {fields.length ? (
                      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[13.5px]">
                        {fields.map((f) => (
                          <div key={f.key} className="contents">
                            <dt className="text-muted-foreground">{f.header}</dt>
                            <dd className={cn('break-words', f.align === 'right' && 'font-mono')}>{f.cell(row)}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                    {actions && !onRowClick ? <div className="mt-2">{actions.cell(row)}</div> : null}
                  </>
                )
                return (
                  <li key={k} aria-selected={onRowClick ? k === selectedKey : undefined}>
                    {onRowClick ? (
                      <button
                        type="button"
                        onClick={activate(row)}
                        className={cn(
                          'block w-full px-4 py-3.5 text-left hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                          k === selectedKey && 'bg-selected',
                        )}
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="px-4 py-3.5">{body}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          </>
        )}
        {paging ? <ListSummary page={paging.page} pageSize={paging.pageSize} totalItems={paging.total} isBusy={Boolean(loading)} /> : null}
      </div>
      {paging ? (
        <ListPagination
          page={paging.page}
          pageSize={paging.pageSize}
          pageSizeOptions={paging.pageSizeOptions}
          totalItems={paging.total}
          isBusy={Boolean(loading)}
          onPageChange={paging.onPage}
          onPageSizeChange={paging.onPageSize}
        />
      ) : null}
    </>
  )
}
